/**
 * Media Gallery Component
 * Browse, search, filter, and select media from the library
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MediaItem, MediaType } from '../../types';
import {
  subscribeToMediaItems,
  deleteMediaItem,
  renameMediaItem,
  trackMediaUsage,
  formatFileSize,
  getFileIcon,
  canPreview,
  getAllTags,
  getAllLabels,
} from '../../utils/mediaLibrary';
import { format } from 'date-fns';
import MediaUploader from './MediaUploader';
import { Folder, Copy, Link, Trash2, Pencil, Check, X } from 'lucide-react';
import './MediaGallery.css';

interface MediaGalleryProps {
  onSelect?: (item: MediaItem) => void;
  onSelectMultiple?: (items: MediaItem[]) => void;
  selectionMode?: boolean;
  selectionModeMulti?: boolean;
  allowedTypes?: MediaType[];
  showUploader?: boolean;
}

export const MediaGallery: React.FC<MediaGalleryProps> = ({
  onSelect,
  onSelectMultiple,
  selectionMode = false,
  selectionModeMulti = false,
  allowedTypes,
  showUploader = true,
}) => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<MediaType | 'all'>('all');
  const [filterTag, setFilterTag] = useState<string>('');
  const [filterLabel, setFilterLabel] = useState<string>('');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'usage'>('date');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [selectedItems, setSelectedItems] = useState<MediaItem[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [allLabels, setAllLabels] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Focus rename input when editing starts
  useEffect(() => {
    if (editingName && renameInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        renameInputRef.current?.focus();
        renameInputRef.current?.select();
      }, 50);
    }
  }, [editingName]);

  // Subscribe to media items
  useEffect(() => {
    const unsubscribe = subscribeToMediaItems(
      (mediaItems) => {
        setItems(mediaItems);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error loading media:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load tags and labels
  useEffect(() => {
    getAllTags().then(setAllTags);
    getAllLabels().then(setAllLabels);
  }, [items]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let result = [...items];

    // Filter by allowed types
    if (allowedTypes && allowedTypes.length > 0) {
      result = result.filter((item) => allowedTypes.includes(item.type));
    }

    // Filter by type
    if (filterType !== 'all') {
      result = result.filter((item) => item.type === filterType);
    }

    // Filter by tag
    if (filterTag) {
      result = result.filter((item) => item.tags.includes(filterTag));
    }

    // Filter by label
    if (filterLabel) {
      result = result.filter((item) => item.labels.includes(filterLabel));
    }

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    // Sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'usage':
        result.sort((a, b) => b.usageCount - a.usageCount);
        break;
      case 'date':
      default:
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    return result;
  }, [items, filterType, filterTag, filterLabel, searchQuery, sortBy, allowedTypes]);

  const handleItemClick = useCallback((item: MediaItem) => {
    if (selectionModeMulti) {
      setSelectedItems((prev) => {
        const exists = prev.some((p) => p.id === item.id);
        if (exists) return prev.filter((p) => p.id !== item.id);
        return [...prev, item];
      });
    } else if (selectionMode) {
      setSelectedItem(item);
    } else {
      setSelectedItem(item);
      setShowPreview(true);
    }
  }, [selectionMode, selectionModeMulti]);

  const handleItemDoubleClick = useCallback((item: MediaItem) => {
    // Double-click always opens preview (even in selection mode)
    setSelectedItem(item);
    setShowPreview(true);
  }, []);

  const handleSelect = useCallback(async () => {
    if (selectionModeMulti && selectedItems.length > 0 && onSelectMultiple) {
      for (const it of selectedItems) {
        if (it.id) await trackMediaUsage(it.id);
      }
      onSelectMultiple(selectedItems);
    } else if (selectedItem && onSelect) {
      if (selectedItem.id) await trackMediaUsage(selectedItem.id);
      onSelect(selectedItem);
    }
  }, [selectedItem, selectedItems, onSelect, onSelectMultiple, selectionModeMulti]);

  const handleDelete = async (id: string) => {
    try {
      await deleteMediaItem(id);
      setDeleteConfirm(null);
      if (selectedItem?.id === id) {
        setSelectedItem(null);
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Error deleting media:', error);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    // Could add a toast notification here
  };

  const handleStartRename = () => {
    if (selectedItem) {
      setNewName(selectedItem.name);
      setEditingName(true);
    }
  };

  const handleSaveRename = async () => {
    if (selectedItem?.id && newName.trim() && newName !== selectedItem.name) {
      try {
        await renameMediaItem(selectedItem.id, newName.trim());
        setEditingName(false);
      } catch (error) {
        console.error('Error renaming file:', error);
      }
    } else {
      setEditingName(false);
    }
  };

  const handleCancelRename = () => {
    setEditingName(false);
    setNewName('');
  };

  return (
    <div className="media-gallery">
      {/* Header */}
      <div className="gallery-header">
        <h2>Media Library</h2>
        {showUploader && (
          <button className="upload-trigger-btn" onClick={() => setShowUploadModal(true)}>
            + Upload Media
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="gallery-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search media..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              ×
            </button>
          )}
        </div>

        <div className="filter-group">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value as MediaType | 'all')}>
            <option value="all">All Types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="document">Documents</option>
            <option value="model">3D Models</option>
          </select>

          <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
            <option value="">All Tags</option>
            {allTags.map((tag) => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>

          <select value={filterLabel} onChange={(e) => setFilterLabel(e.target.value)}>
            <option value="">All Labels</option>
            {allLabels.map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
        </div>

        <div className="view-controls">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'usage')}>
            <option value="date">Newest First</option>
            <option value="name">Name A-Z</option>
            <option value="usage">Most Used</option>
          </select>

          <div className="view-toggle">
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              ⊞
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="gallery-results-info">
        {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
        {(filterTag || filterLabel || searchQuery) && (
          <button className="clear-filters" onClick={() => {
            setSearchQuery('');
            setFilterTag('');
            setFilterLabel('');
            setFilterType('all');
          }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Gallery */}
      {isLoading ? (
        <div className="gallery-loading">Loading media...</div>
      ) : filteredItems.length === 0 ? (
        <div className="gallery-empty">
          <div className="empty-icon"><Folder size={48} /></div>
          <div className="empty-text">No media found</div>
          {showUploader && (
            <button className="empty-upload-btn" onClick={() => setShowUploadModal(true)}>
              Upload your first media
            </button>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <div
            className={`gallery-content ${viewMode}`}
            style={viewMode === 'grid' ? {
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px',
              padding: '16px 20px',
            } : undefined}
          >
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={`media-item ${
                  selectionModeMulti ? (selectedItems.some((s) => s.id === item.id) ? 'selected' : '') :
                  selectedItem?.id === item.id ? 'selected' : ''
                }`}
                onClick={() => handleItemClick(item)}
                onDoubleClick={() => handleItemDoubleClick(item)}
              >
                {viewMode === 'grid' ? (
                  <div style={{ width: '100%', height: '160px', overflow: 'hidden', background: 'var(--color-surface)', borderRadius: '8px 8px 0 0', position: 'relative' }}>
                    {item.type === 'image' && item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt={item.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
                    ) : item.type === 'image' ? (
                      <img src={item.url} alt={item.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
                    ) : item.type === 'video' ? (
                      <>
                        <video
                          src={item.url}
                          muted
                          playsInline
                          preload="metadata"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onLoadedMetadata={(e) => {
                            const vid = e.currentTarget;
                            vid.currentTime = 0.5;
                          }}
                        />
                        <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', borderRadius: 4, padding: '2px 6px', fontSize: 10, color: '#fff', fontWeight: 600, letterSpacing: '0.03em' }}>
                          VIDEO
                        </div>
                      </>
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>
                        {getFileIcon(item.mimeType, item.name)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="item-preview">
                    {item.type === 'image' && item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt={item.name} loading="lazy" />
                    ) : item.type === 'image' ? (
                      <img src={item.url} alt={item.name} loading="lazy" />
                    ) : item.type === 'video' ? (
                      <>
                        <video
                          src={item.url}
                          muted
                          playsInline
                          preload="metadata"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onLoadedMetadata={(e) => {
                            const vid = e.currentTarget;
                            vid.currentTime = 0.5;
                          }}
                        />
                        <div className="item-type-badge">video</div>
                      </>
                    ) : (
                      <span className="item-icon">{getFileIcon(item.mimeType, item.name)}</span>
                    )}
                    {item.type !== 'video' && <div className="item-type-badge">{item.type}</div>}
                  </div>
                )}
                <div className="item-info">
                  <div className="item-name" title={item.name}>{item.name}</div>
                  <div className="item-meta">
                    <span>{formatFileSize(item.size)}</span>
                    {viewMode === 'list' && (
                      <>
                        <span>•</span>
                        <span>{format(new Date(item.createdAt), 'MMM d, yyyy')}</span>
                        <span>•</span>
                        <span>Used {item.usageCount}x</span>
                      </>
                    )}
                  </div>
                  {viewMode === 'list' && item.tags.length > 0 && (
                    <div className="item-tags">
                      {item.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="item-tag">{tag}</span>
                      ))}
                      {item.tags.length > 3 && <span className="item-tag more">+{item.tags.length - 3}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selection Mode Actions */}
      {selectionModeMulti && selectedItems.length > 0 && (
        <div className="selection-bar">
          <div className="selection-info">
            {selectedItems.length} selected
          </div>
          <div className="selection-actions">
            <button className="cancel-btn" onClick={() => setSelectedItems([])}>
              Clear
            </button>
            <button className="select-btn" onClick={handleSelect}>
              Add {selectedItems.length} to Gallery
            </button>
          </div>
        </div>
      )}
      {selectionMode && !selectionModeMulti && selectedItem && (
        <div className="selection-bar">
          <div className="selection-info">
            Selected: <strong>{selectedItem.name}</strong>
          </div>
          <div className="selection-actions">
            <button className="details-btn" onClick={() => setShowPreview(true)} title="View details & rename">
              <Pencil size={14} />
              Edit
            </button>
            <button className="cancel-btn" onClick={() => setSelectedItem(null)}>
              Cancel
            </button>
            <button className="select-btn" onClick={handleSelect}>
              Use Selected Media
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedItem && (
        <div className="preview-modal" onClick={() => { setShowPreview(false); setEditingName(false); }}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-preview" onClick={() => { setShowPreview(false); setEditingName(false); }}>×</button>

            <div className="preview-media">
              {selectedItem.type === 'image' ? (
                <img src={selectedItem.url} alt={selectedItem.name} />
              ) : selectedItem.type === 'video' ? (
                <video src={selectedItem.url} controls autoPlay />
              ) : canPreview(selectedItem.mimeType) ? (
                <iframe src={selectedItem.url} title={selectedItem.name} />
              ) : (
                <div className="preview-icon">{getFileIcon(selectedItem.mimeType, selectedItem.name)}</div>
              )}
            </div>

            <div className="preview-details">
              <div className="preview-title">
                {editingName ? (
                  <div className="rename-input-group" onClick={(e) => e.stopPropagation()}>
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') handleSaveRename();
                        if (e.key === 'Escape') handleCancelRename();
                      }}
                      className="rename-input"
                      placeholder="Enter new name..."
                    />
                    <button className="rename-btn save" onClick={(e) => { e.stopPropagation(); handleSaveRename(); }} title="Save">
                      <Check size={16} />
                    </button>
                    <button className="rename-btn cancel" onClick={(e) => { e.stopPropagation(); handleCancelRename(); }} title="Cancel">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <h3>{selectedItem.name}</h3>
                    <button className="rename-trigger" onClick={handleStartRename} title="Rename file">
                      <Pencil size={14} />
                    </button>
                  </>
                )}
              </div>
              {selectedItem.description && <p className="preview-description">{selectedItem.description}</p>}

              <div className="preview-meta">
                <div className="meta-row">
                  <span className="meta-label">Type:</span>
                  <span>{selectedItem.type}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Size:</span>
                  <span>{formatFileSize(selectedItem.size)}</span>
                </div>
                {selectedItem.width && selectedItem.height && (
                  <div className="meta-row">
                    <span className="meta-label">Dimensions:</span>
                    <span>{selectedItem.width} × {selectedItem.height}</span>
                  </div>
                )}
                <div className="meta-row">
                  <span className="meta-label">Uploaded:</span>
                  <span>{format(new Date(selectedItem.createdAt), 'MMM d, yyyy h:mm a')}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">By:</span>
                  <span>{selectedItem.uploadedByName}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Used:</span>
                  <span>{selectedItem.usageCount} times</span>
                </div>
              </div>

              {selectedItem.tags.length > 0 && (
                <div className="preview-tags">
                  <span className="tags-label">Tags:</span>
                  {selectedItem.tags.map((tag) => (
                    <span key={tag} className="preview-tag">{tag}</span>
                  ))}
                </div>
              )}

              {selectedItem.labels.length > 0 && (
                <div className="preview-labels">
                  <span className="labels-label">Labels:</span>
                  {selectedItem.labels.map((label) => (
                    <span key={label} className="preview-label">{label}</span>
                  ))}
                </div>
              )}

              <div className="preview-actions">
                <button className="action-btn" onClick={() => handleCopyUrl(selectedItem.url)}>
                  <Copy size={16} /> Copy URL
                </button>
                <a href={selectedItem.url} target="_blank" rel="noopener noreferrer" className="action-btn">
                  <Link size={16} /> Open Original
                </a>
                {deleteConfirm === selectedItem.id ? (
                  <div className="delete-confirm">
                    <span>Delete this media?</span>
                    <button className="confirm-delete" onClick={() => handleDelete(selectedItem.id!)}>
                      Yes, Delete
                    </button>
                    <button className="cancel-delete" onClick={() => setDeleteConfirm(null)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button className="action-btn danger" onClick={() => setDeleteConfirm(selectedItem.id!)}>
                    <Trash2 size={16} /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3>Upload Media</h3>
              <button className="close-modal" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            <MediaUploader
              onUploadComplete={(item) => {
                // Don't close modal - let user upload more if they want
              }}
              onCancel={() => setShowUploadModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
